/**
 * useRankingQuery - Hook para dados de ranking
 * 
 * Encapsula chamadas ao rankingService com cache do React Query.
 */

import { useQuery } from '@tanstack/react-query';
import rankingService from '../../services/rankingService';

// ==================== QUERY KEYS ====================
export const rankingKeys = {
    all: ['ranking'],
    globalRanking: () => [...rankingKeys.all, 'global'],
    globalTop10: () => [...rankingKeys.all, 'top10'],
    globalTop3: () => [...rankingKeys.all, 'top3'],
    challengeRanking: (challengeId) => [...rankingKeys.all, 'challenge', challengeId],
    challengeTop3: (challengeId) => [...rankingKeys.all, 'challengeTop3', challengeId],
    userPosition: (userId) => [...rankingKeys.all, 'userPosition', userId],
};

// ==================== HOOKS ====================

/**
 * Hook para buscar ranking global completo (Top 100)
 */
export const useGlobalRanking = (enabled = true) => {
    return useQuery({
        queryKey: rankingKeys.globalRanking(),
        queryFn: () => rankingService.getGlobalRanking(),
        staleTime: 45 * 60 * 1000, // 45 minutos (ranking muda pouco)
        // Sempre revalida ao montar: um [] persistido (ex.: fetch sem sessão/RLS)
        // seria servido como "sucesso fresco" por até 24h e nunca se recuperaria.
        refetchOnMount: 'always',
        enabled,
    });
};

/**
 * Hook para buscar Top 10 do ranking global
 */
export const useGlobalTop10 = (enabled = true) => {
    return useQuery({
        queryKey: rankingKeys.globalTop10(),
        queryFn: () => rankingService.getGlobalTop10(),
        staleTime: 40 * 60 * 1000, // 40 minutos
        // Ver useGlobalRanking: evita servir um [] persistido para sempre.
        refetchOnMount: 'always',
        enabled,
    });
};

/**
 * Hook para buscar Top 3 do ranking global (Pódio)
 */
export const useGlobalTop3 = (enabled = true) => {
    return useQuery({
        queryKey: rankingKeys.globalTop3(),
        queryFn: () => rankingService.getGlobalTop3(),
        staleTime: 35 * 60 * 1000, // 35 minutos
        enabled,
    });
};

/**
 * Hook para buscar ranking de um desafio específico
 */
export const useChallengeRanking = (challengeId, options = {}) => {
    const { enabled = true, initialData = undefined } = options;
    return useQuery({
        queryKey: rankingKeys.challengeRanking(challengeId),
        queryFn: () => rankingService.getChallengeRanking(challengeId),
        staleTime: 35 * 60 * 1000, // 35 minutos
        enabled: enabled && !!challengeId,
        initialData,
        placeholderData: initialData,
    });
};

/**
 * Hook para buscar Top 3 de um desafio
 */
export const useChallengeTop3 = (challengeId, enabled = true) => {
    return useQuery({
        queryKey: rankingKeys.challengeTop3(challengeId),
        queryFn: () => rankingService.getChallengeTop3(challengeId),
        staleTime: 35 * 60 * 1000, // 35 minutos
        enabled: enabled && !!challengeId,
    });
};

/**
 * Hook para buscar posição real do usuário
 */
export const useUserRealPosition = (userId, enabled = true) => {
    return useQuery({
        queryKey: rankingKeys.userPosition(userId),
        queryFn: () => rankingService.getUserRealPosition(userId),
        staleTime: 5 * 60 * 1000, // 5 minutos (posição muda mais)
        enabled: enabled && !!userId,
    });
};

/**
 * Hook para buscar desafios ativos
 */
export const useActiveChallenges = (enabled = true) => {
    return useQuery({
        queryKey: ['challenges', 'active'],
        queryFn: () => rankingService.getActiveChallenges(),
        staleTime: 10 * 60 * 1000, // 10 minutos
        enabled,
    });
};
